import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadIcon from '@mui/icons-material/Upload';
import VideocamIcon from '@mui/icons-material/Videocam';
import type { TenantSiteEditorSectionId } from '../../../components/tenantSite/TenantSiteRenderer';
import { useTenant } from '../../../tenancy/TenantContext';
import {
  HERO_VIDEO_REQUIREMENTS,
  type HeroVideoUploadStage,
  uploadTenantSiteAsset,
  uploadTenantSiteVideo,
} from '../../../sitePublishing/assets';
import { PublicBusinessHours, PublishValidationIssue, SiteDraft } from '../../../sitePublishing/types';
import { replaceGalleryImageAtIndex, type SiteDraftUpdate } from './draftWorkspaceState';
import { SITE_EDITOR_SECTION_MAP } from './siteEditorSections';
import { SitePresentationSettings } from './sitePresentationSettings';
import SiteBuilderServicesPanel from './SiteBuilderServicesPanel';
import SiteBuilderGalleryPanel from './SiteBuilderGalleryPanel';

const DAYS: Array<keyof PublicBusinessHours> = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];

const DAY_LABELS: Record<keyof PublicBusinessHours, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

type AssetKey = keyof SiteDraft['assets'];

export type SiteDraftEditorProps = {
  draft: SiteDraft;
  disabled: boolean;
  issues: PublishValidationIssue[];
  sectionId: TenantSiteEditorSectionId;
  presentationSettings: SitePresentationSettings;
  onChange: (update: SiteDraftUpdate) => void;
  onPresentationSettingsChange: (settings: SitePresentationSettings) => void;
  selectedGalleryIndex?: number;
};

const cloneDraft = (draft: SiteDraft): SiteDraft => JSON.parse(JSON.stringify(draft)) as SiteDraft;
const validColor = (value: string): boolean => /^#[0-9a-fA-F]{6}$/.test(value);

let googlePlacesScriptPromise: Promise<void> | undefined;

const loadGooglePlaces = (key: string): Promise<void> => {
  if ((window as any).google?.maps?.places) return Promise.resolve();
  if (googlePlacesScriptPromise) return googlePlacesScriptPromise;
  googlePlacesScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById('scheduify-google-places') as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => {
      googlePlacesScriptPromise = undefined;
      reject(new Error('Google Places could not be loaded.'));
    }, { once: true });
    if (!existing) {
      script.id = 'scheduify-google-places';
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&libraries=places&v=weekly`;
      document.head.appendChild(script);
    }
  });
  return googlePlacesScriptPromise;
};

// The FAQ section needs at least this many answered questions to be shown.
const MIN_FAQ_QUESTIONS = 2;

const ToggleRow: React.FC<{
  label: string;
  detail?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, detail, checked, disabled, onChange }) => (
  <Box
    sx={{
      px: 1.75,
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2.5,
      bgcolor: 'background.paper',
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700}>{label}</Typography>
      {detail ? <Typography variant="caption" color="text.secondary">{detail}</Typography> : null}
    </Box>
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      inputProps={{ 'aria-label': label }}
    />
  </Box>
);

const ColorField: React.FC<{
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, disabled, onChange }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
      <Box
        component="input"
        type="color"
        aria-label={`${label} color picker`}
        value={validColor(value) ? value : '#000000'}
        disabled={disabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        sx={{
          width: 46,
          height: 40,
          p: 0.4,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
      <TextField
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        error={!validColor(value)}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': `${label} hex value`, maxLength: 7 }}
      />
    </Stack>
  </Box>
);

const GooglePlaceField: React.FC<{
  address: string;
  placeId?: string;
  disabled: boolean;
  onSelect: (address: string, placeId?: string) => void;
}> = ({ address, placeId, disabled, onSelect }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const placesLibraryRef = useRef<any>();
  const sessionTokenRef = useRef<any>();
  const onSelectRef = useRef(onSelect);
  const [placesReady, setPlacesReady] = useState(false);
  const [searching, setSearching] = useState(false);
  const [focused, setFocused] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [loadError, setLoadError] = useState(false);
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    setLoadError(false);
    void loadGooglePlaces(key).then(async () => {
      if (cancelled) return;
      const library = await (window as any).google.maps.importLibrary('places');
      if (cancelled) return;
      if (!library?.AutocompleteSuggestion || !library?.AutocompleteSessionToken) {
        throw new Error('Google Places (New) is unavailable.');
      }
      placesLibraryRef.current = library;
      sessionTokenRef.current = new library.AutocompleteSessionToken();
      setPlacesReady(true);
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });
    return () => {
      cancelled = true;
      placesLibraryRef.current = undefined;
      sessionTokenRef.current = undefined;
    };
  }, [key]);

  useEffect(() => {
    const query = address.trim();
    if (!placesReady || disabled || placeId || query.length < 2) {
      setPredictions([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timeout = window.setTimeout(async () => {
      try {
        const response = await placesLibraryRef.current.AutocompleteSuggestion
          .fetchAutocompleteSuggestions({
            input: query,
            sessionToken: sessionTokenRef.current,
            pureServiceAreaBusinessesIncluded: true,
          });
        if (cancelled) return;
        setPredictions((response.suggestions ?? [])
          .flatMap((suggestion: any) => suggestion.placePrediction ? [suggestion.placePrediction] : [])
          .slice(0, 6));
        setLoadError(false);
      } catch {
        if (!cancelled) {
          setPredictions([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [address, disabled, placeId, placesReady]);

  const helperText = placeId
    ? 'Google location selected'
    : loadError
      ? 'Location is optional; you can save and publish without it.'
      : key
        ? 'Optional. Choose a result to add a map.'
        : 'Optional. Google search is not configured.';

  return (
    <Box ref={anchorRef} sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        label="Business location (optional)"
        value={address}
        disabled={disabled}
        autoComplete="off"
        helperText={helperText}
        onFocus={() => setFocused(true)}
        onBlur={() => window.setTimeout(() => setFocused(false), 100)}
        onChange={(event) => onSelectRef.current(event.target.value)}
        slotProps={{
          htmlInput: {
            autoComplete: 'new-password',
            'aria-label': 'Search for your business location',
            'aria-autocomplete': 'list',
            'aria-expanded': focused && predictions.length > 0,
          },
          input: {
            endAdornment: searching ? <CircularProgress size={20} /> : undefined,
          },
        }}
      />
      {loadError ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Google search is unavailable. The site can still be saved and published without a location.
        </Alert>
      ) : null}
      <Popper
        open={focused && predictions.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1600, width: anchorRef.current?.clientWidth }}
      >
        <Paper elevation={10} sx={{ mt: 0.5, overflow: 'hidden', borderRadius: 2 }}>
          <List disablePadding aria-label="Google business results">
            {predictions.map((prediction) => {
              const main = prediction.mainText?.text ?? prediction.text?.text ?? '';
              const secondary = prediction.secondaryText?.text ?? '';
              return (
                <ListItemButton
                  key={prediction.placeId}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={async () => {
                    setSearching(true);
                    try {
                      const place = prediction.toPlace();
                      await place.fetchFields({ fields: ['formattedAddress'] });
                      onSelectRef.current(
                        place.formattedAddress || secondary || prediction.text?.text || '',
                        prediction.placeId
                      );
                      sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
                      setPredictions([]);
                      setFocused(false);
                      setLoadError(false);
                    } catch {
                      setLoadError(true);
                    } finally {
                      setSearching(false);
                    }
                  }}
                  sx={{ py: 1.1, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={main}
                    secondary={secondary}
                    primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }}
                    secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1, textAlign: 'right' }}>
            Powered by Google
          </Typography>
        </Paper>
      </Popper>
    </Box>
  );
};

const SiteDraftEditor: React.FC<SiteDraftEditorProps> = ({
  draft,
  disabled,
  issues,
  sectionId,
  presentationSettings,
  onChange,
  onPresentationSettingsChange,
  selectedGalleryIndex,
}) => {
  const { tenantId } = useTenant();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadError, setUploadError] = useState<string>();
  const [heroVideoUpload, setHeroVideoUpload] = useState<{ stage: HeroVideoUploadStage; progress: number }>();
  const [heroVideoError, setHeroVideoError] = useState<string>();
  const [confirmRemoveHeroVideo, setConfirmRemoveHeroVideo] = useState(false);
  const section = SITE_EDITOR_SECTION_MAP[sectionId];
  const galleryReplacementInProgress = Object.keys(uploadProgress)
    .some((key) => /^gallery-\d+$/.test(key));
  useEffect(() => {
    if (sectionId !== 'gallery' || selectedGalleryIndex === undefined) return;
    document.querySelector(`[data-gallery-editor-index="${selectedGalleryIndex}"]`)
      ?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }, [sectionId, selectedGalleryIndex]);

  const change = (mutate: (next: SiteDraft) => void) => {
    onChange((current) => {
      const next = cloneDraft(current);
      mutate(next);
      return next;
    });
  };

  const changePresentation = <Key extends keyof SitePresentationSettings>(
    key: Key,
    value: SitePresentationSettings[Key]
  ) => onPresentationSettingsChange({ ...presentationSettings, [key]: value });

  const helperFor = (path: string): string | undefined =>
    issues.find((entry) => entry.path === path || entry.path?.startsWith(`${path}.`))?.message;

  const uploadAsset = async (key: AssetKey, file: File) => {
    setUploadError(undefined);
    setUploadProgress((current) => ({ ...current, [key]: 0 }));
    try {
      const path = await uploadTenantSiteAsset(tenantId, file, (percentage) => {
        setUploadProgress((current) => ({ ...current, [key]: percentage }));
      });
      change((next) => { next.assets[key] = path; });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Image upload failed.');
    } finally {
      setUploadProgress((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  const uploadGalleryImage = async (index: number, file: File) => {
    const progressKey = `gallery-${index}`;
    const originalImage = draft.homepage.gallery[index]?.image;
    setUploadError(undefined);
    setUploadProgress((current) => ({ ...current, [progressKey]: 0 }));
    try {
      const path = await uploadTenantSiteAsset(tenantId, file, (percentage) => {
        setUploadProgress((current) => ({ ...current, [progressKey]: percentage }));
      });
      onChange((current) => replaceGalleryImageAtIndex(current, index, originalImage, path));
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Gallery image upload failed.');
    } finally {
      setUploadProgress((current) => {
        const next = { ...current };
        delete next[progressKey];
        return next;
      });
    }
  };

  const uploadNewGalleryImage = async (file: File) => {
    const progressKey = 'gallery-new';
    setUploadError(undefined);
    setUploadProgress((current) => ({ ...current, [progressKey]: 0 }));
    try {
      const path = await uploadTenantSiteAsset(tenantId, file, (percentage) => {
        setUploadProgress((current) => ({ ...current, [progressKey]: percentage }));
      });
      change((next) => { next.homepage.gallery.push({ image: path }); });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Gallery image upload failed.');
    } finally {
      setUploadProgress((current) => {
        const next = { ...current };
        delete next[progressKey];
        return next;
      });
    }
  };

  const assetField = (key: AssetKey, label: string, detail?: string) => {
    const progress = uploadProgress[key];
    const hasAsset = Boolean(draft.assets[key]);
    return (
      <Box sx={{ p: 1.75, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2,
              color: hasAsset ? 'success.dark' : 'text.secondary',
              bgcolor: 'action.hover',
            }}
          >
            <AddPhotoAlternateIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700}>{label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {progress !== undefined ? `Uploading ${progress}%` : hasAsset ? 'Image selected' : detail || 'No image'}
            </Typography>
          </Box>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            disabled={disabled || progress !== undefined}
          >
            {hasAsset ? 'Replace' : 'Upload'}
            <input
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void uploadAsset(key, file);
                event.target.value = '';
              }}
            />
          </Button>
        </Stack>
        {hasAsset ? (
          <Button
            size="small"
            color="error"
            disabled={disabled || progress !== undefined}
            sx={{ mt: 1, ml: 5.5 }}
            onClick={() => change((next) => { delete next.assets[key]; })}
          >
            Remove
          </Button>
        ) : null}
      </Box>
    );
  };

  const uploadHeroVideo = async (file: File) => {
    setHeroVideoError(undefined);
    setHeroVideoUpload({ stage: 'checking', progress: 0 });
    try {
      const path = await uploadTenantSiteVideo(
        tenantId,
        file,
        (progress) => setHeroVideoUpload({ stage: 'uploading', progress }),
        (stage) => setHeroVideoUpload((current) => ({ stage, progress: current?.progress ?? 0 }))
      );
      change((next) => {
        next.assets.heroVideo = path;
        next.homepage.heroMediaType = 'video';
      });
    } catch (error) {
      setHeroVideoError(error instanceof Error ? error.message : 'Video upload failed.');
    } finally {
      setHeroVideoUpload(undefined);
    }
  };

  // Removing only clears the draft. The file is deleted by the regular cleanup
  // once neither the draft nor the published site uses it.
  const removeHeroVideo = () => {
    change((next) => {
      delete next.assets.heroVideo;
      next.homepage.heroMediaType = 'image';
    });
    setConfirmRemoveHeroVideo(false);
  };

  const heroVideoField = () => {
    const hasVideo = Boolean(draft.assets.heroVideo);
    const busy = heroVideoUpload !== undefined;
    const status = heroVideoUpload?.stage === 'checking'
      ? 'Checking video…'
      : heroVideoUpload ? `Uploading ${heroVideoUpload.progress}%`
        : hasVideo ? 'Video selected' : 'No video';
    const helper = helperFor('assets.heroVideo');
    return (
      <Box sx={{ p: 1.75, border: '1px solid', borderColor: helper ? 'error.main' : 'divider', borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 2,
              color: hasVideo ? 'success.dark' : 'text.secondary',
              bgcolor: 'action.hover',
            }}
          >
            <VideocamIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700}>Background video</Typography>
            <Typography variant="caption" color="text.secondary">{status}</Typography>
          </Box>
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            disabled={disabled || busy}
          >
            {hasVideo ? 'Replace' : 'Upload'}
            <input
              hidden
              type="file"
              accept="video/mp4"
              aria-label="Background video file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void uploadHeroVideo(file);
                event.target.value = '';
              }}
            />
          </Button>
        </Stack>
        {busy ? (
          <LinearProgress
            sx={{ mt: 1.25 }}
            variant={heroVideoUpload?.stage === 'uploading' ? 'determinate' : 'indeterminate'}
            value={heroVideoUpload?.progress ?? 0}
            aria-label={status}
          />
        ) : null}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {HERO_VIDEO_REQUIREMENTS} It plays muted and on a loop, and your background image shows
          until it starts, when it cannot play, and for visitors who reduce motion.
        </Typography>
        {heroVideoError || helper ? (
          <Alert severity="error" sx={{ mt: 1 }} onClose={heroVideoError ? () => setHeroVideoError(undefined) : undefined}>
            {heroVideoError || helper}
          </Alert>
        ) : null}
        {hasVideo ? (
          <Button
            size="small"
            color="error"
            disabled={disabled || busy}
            sx={{ mt: 1, ml: 5.5 }}
            onClick={() => setConfirmRemoveHeroVideo(true)}
          >
            Remove video
          </Button>
        ) : null}
      </Box>
    );
  };

  const renderSection = () => {
    switch (sectionId) {
      case 'branding':
        return (
          <Stack spacing={2}>
            <TextField
              label="Business name"
              value={draft.business.displayName}
              disabled={disabled}
              error={Boolean(helperFor('business.displayName'))}
              helperText={helperFor('business.displayName')}
              onChange={(event) => change((next) => { next.business.displayName = event.target.value; })}
            />
            <ToggleRow
              label="Show logo in navbar"
              checked={draft.homepage.navbarLogoVisible}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.homepage.navbarLogoVisible = checked; })}
            />
            {assetField('logo', 'Business logo', 'PNG or WebP works best')}
          </Stack>
        );

      case 'hero':
        return (
          <Stack spacing={2}>
            <TextField
              label="Headline"
              value={draft.homepage.heroTitle}
              disabled={disabled}
              onChange={(event) => change((next) => { next.homepage.heroTitle = event.target.value; })}
            />
            <TextField
              label="Supporting text"
              multiline
              minRows={2}
              value={draft.homepage.heroSubtitle}
              disabled={disabled}
              onChange={(event) => change((next) => { next.homepage.heroSubtitle = event.target.value; })}
            />
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Background</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={draft.homepage.heroMediaType ?? 'image'}
                disabled={disabled}
                aria-label="Spotlight background"
                onChange={(_, value: 'image' | 'video' | null) => {
                  if (value) change((next) => { next.homepage.heroMediaType = value; });
                }}
              >
                <ToggleButton value="image">Image</ToggleButton>
                <ToggleButton value="video">Video</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {draft.homepage.heroMediaType === 'video' ? heroVideoField() : null}
            {assetField(
              'hero',
              draft.homepage.heroMediaType === 'video' ? 'Poster image' : 'Background image',
              draft.homepage.heroMediaType === 'video' ? 'Required: shows before and instead of the video' : 'Wide images work best'
            )}
            {draft.homepage.heroMediaType === 'video' && helperFor('assets.hero') ? (
              <Alert severity="error">{helperFor('assets.hero')}</Alert>
            ) : null}
            <ToggleRow
              label="Show logo in spotlight"
              checked={draft.homepage.spotlightLogoVisible}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.homepage.spotlightLogoVisible = checked; })}
            />
            {draft.homepage.spotlightLogoVisible ? assetField('heroLogo', 'Spotlight logo', 'Optional alternate logo') : null}
          </Stack>
        );

      case 'services':
        return <SiteBuilderServicesPanel tenantId={tenantId} disabled={disabled} />;

      case 'gallery':
        return (
          <Stack spacing={2}>
            <ToggleRow
              label="Show gallery"
              checked={draft.sections.showGallery}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.sections.showGallery = checked; })}
            />
            <SiteBuilderGalleryPanel
              gallery={draft.homepage.gallery}
              disabled={disabled}
              previewSelectedIndex={selectedGalleryIndex}
              uploadProgress={uploadProgress}
              replacementInProgress={galleryReplacementInProgress}
              onUploadNew={(file) => void uploadNewGalleryImage(file)}
              onReplace={(index, file) => void uploadGalleryImage(index, file)}
              onUpdate={(index, field, value) => change((next) => { next.homepage.gallery[index][field] = value; })}
              onRemove={(index) => change((next) => { next.homepage.gallery.splice(index, 1); })}
              onMove={(from, to) => change((next) => {
                const [moved] = next.homepage.gallery.splice(from, 1);
                next.homepage.gallery.splice(to, 0, moved);
              })}
            />
          </Stack>
        );

      case 'team':
        return (
          <Stack spacing={2}>
            <ToggleRow
              label="Show team"
              detail="Display active, bookable team members"
              checked={draft.sections.showTeam}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.sections.showTeam = checked; })}
            />
          </Stack>
        );

      case 'reviews':
        return (
          <Stack spacing={2}>
            <ToggleRow
              label="Show reviews"
              checked={draft.sections.showReviews}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.sections.showReviews = checked; })}
            />
            {draft.homepage.reviews.map((review, index) => (
              <Box key={`${review.name}-${index}`} sx={{ p: 1.75, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    label="Customer name"
                    value={review.name}
                    disabled={disabled}
                    onChange={(event) => change((next) => {
                      next.homepage.reviews[index].name = event.target.value;
                    })}
                  />
                  <TextField
                    size="small"
                    label="Review"
                    multiline
                    minRows={3}
                    value={review.text}
                    disabled={disabled}
                    onChange={(event) => change((next) => {
                      next.homepage.reviews[index].text = event.target.value;
                    })}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      size="small"
                      label="Rating"
                      value={review.rating}
                      disabled={disabled}
                      sx={{ width: 120 }}
                      onChange={(event) => change((next) => {
                        next.homepage.reviews[index].rating = Number(event.target.value);
                      })}
                    >
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <MenuItem key={rating} value={rating}>{rating} stars</MenuItem>
                      ))}
                    </TextField>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      disabled={disabled}
                      onClick={() => change((next) => { next.homepage.reviews.splice(index, 1); })}
                    >
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
            <Button
              variant="outlined"
              disabled={disabled || draft.homepage.reviews.length >= 30}
              onClick={() => change((next) => {
                next.homepage.reviews.push({ name: '', text: '', rating: 5 });
              })}
            >
              Add review
            </Button>
          </Stack>
        );

      case 'about':
        return (
          <Stack spacing={2}>
            <ToggleRow
              label="Show About"
              checked={draft.sections.showAbout}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.sections.showAbout = checked; })}
            />
            <TextField
              label="About your business"
              multiline
              minRows={6}
              value={draft.homepage.about ?? ''}
              disabled={disabled}
              onChange={(event) => change((next) => { next.homepage.about = event.target.value; })}
            />
          </Stack>
        );

      case 'faq': {
        const faq = draft.homepage.faq ?? [];
        const completeQuestions = faq.filter((item) => item.question.trim() && item.answer.trim()).length;
        const canShowFaq = completeQuestions >= MIN_FAQ_QUESTIONS;
        return (
          <Stack spacing={2}>
            <ToggleRow
              label="Show FAQ"
              detail={canShowFaq
                ? 'Answer the questions customers ask most'
                : `Add at least ${MIN_FAQ_QUESTIONS} questions with answers to show it`}
              checked={draft.sections.showFaq === true && canShowFaq}
              disabled={disabled || !canShowFaq}
              onChange={(checked) => change((next) => { next.sections.showFaq = checked; })}
            />
            {faq.map((item, index) => (
              <Box key={index} sx={{ p: 1.75, border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
                <Stack spacing={1.5}>
                  <TextField
                    size="small"
                    label="Question"
                    value={item.question}
                    disabled={disabled}
                    inputProps={{ maxLength: 200 }}
                    error={Boolean(helperFor(`homepage.faq.${index}.question`))}
                    helperText={helperFor(`homepage.faq.${index}.question`)}
                    onChange={(event) => change((next) => {
                      (next.homepage.faq ??= [])[index].question = event.target.value;
                    })}
                  />
                  <TextField
                    size="small"
                    label="Answer"
                    multiline
                    minRows={3}
                    value={item.answer}
                    disabled={disabled}
                    inputProps={{ maxLength: 1000 }}
                    error={Boolean(helperFor(`homepage.faq.${index}.answer`))}
                    helperText={helperFor(`homepage.faq.${index}.answer`)}
                    onChange={(event) => change((next) => {
                      (next.homepage.faq ??= [])[index].answer = event.target.value;
                    })}
                  />
                  <Box>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      disabled={disabled}
                      onClick={() => change((next) => {
                        const remaining = (next.homepage.faq ??= []);
                        remaining.splice(index, 1);
                        if (remaining.length < MIN_FAQ_QUESTIONS) next.sections.showFaq = false;
                      })}
                    >
                      Remove
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ))}
            <Button
              variant="outlined"
              disabled={disabled || faq.length >= 20}
              onClick={() => change((next) => { (next.homepage.faq ??= []).push({ question: '', answer: '' }); })}
            >
              Add question
            </Button>
          </Stack>
        );
      }

      case 'hours':
        return (
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {DAYS.map((day) => (
                <TextField
                  key={day}
                  size="small"
                  label={DAY_LABELS[day]}
                  value={draft.business.businessHours[day]}
                  disabled={disabled}
                  onChange={(event) => change((next) => {
                    next.business.businessHours[day] = event.target.value;
                  })}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              These are display hours, not employee availability.
            </Typography>
          </Stack>
        );

      case 'booking':
        return (
          <Stack spacing={2}>
            <ToggleRow
              label="Enable booking"
              detail="Show booking actions across the site"
              checked={draft.booking.enabled}
              disabled={disabled}
              onChange={(checked) => change((next) => { next.booking.enabled = checked; })}
            />
            <TextField
              label="Button label"
              value={draft.booking.ctaLabel}
              disabled={disabled || !draft.booking.enabled}
              onChange={(event) => change((next) => { next.booking.ctaLabel = event.target.value; })}
            />
            <ToggleRow
              label="Team booking buttons"
              detail="Show a booking action on each team card"
              checked={draft.booking.showEmployeePicker}
              disabled={disabled || !draft.booking.enabled}
              onChange={(checked) => change((next) => { next.booking.showEmployeePicker = checked; })}
            />
            <Divider textAlign="left"><Typography variant="caption">Booking language</Typography></Divider>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              <TextField
                size="small"
                label="Team member"
                value={presentationSettings.resourceSingular}
                disabled={disabled}
                onChange={(event) => changePresentation('resourceSingular', event.target.value)}
              />
              <TextField
                size="small"
                label="Team members"
                value={presentationSettings.resourcePlural}
                disabled={disabled}
                onChange={(event) => changePresentation('resourcePlural', event.target.value)}
              />
            </Box>
            <TextField
              size="small"
              label="Any available label"
              value={presentationSettings.anyAvailableLabel}
              disabled={disabled}
              onChange={(event) => changePresentation('anyAvailableLabel', event.target.value)}
            />
            <TextField
              size="small"
              label="Team picker prompt"
              value={presentationSettings.selectResourceLabel}
              disabled={disabled}
              onChange={(event) => changePresentation('selectResourceLabel', event.target.value)}
            />
            <Alert severity="info">Checkout rules and customer options are in Settings.</Alert>
          </Stack>
        );

      case 'contact':
        return (
          <Stack spacing={2}>
            <GooglePlaceField
              address={draft.business.address ?? ''}
              placeId={draft.business.googlePlaceId}
              disabled={disabled}
              onSelect={(address, placeId) => change((next) => {
                next.business.address = address;
                if (placeId) next.business.googlePlaceId = placeId;
                else delete next.business.googlePlaceId;
              })}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              <TextField
                label="Phone"
                value={draft.business.phone ?? ''}
                disabled={disabled}
                onChange={(event) => change((next) => { next.business.phone = event.target.value; })}
              />
              <TextField
                label="Email"
                type="email"
                value={draft.business.email ?? ''}
                disabled={disabled}
                onChange={(event) => change((next) => { next.business.email = event.target.value; })}
              />
            </Box>
            <TextField
              label="Business website (optional)"
              value={draft.business.website ?? ''}
              disabled={disabled}
              error={Boolean(helperFor('business.website'))}
              helperText={helperFor('business.website') ?? 'Example: https://yourbusiness.com'}
              onChange={(event) => change((next) => {
                const value = event.target.value;
                if (value) next.business.website = value;
                else delete next.business.website;
              })}
            />
            <Divider><Typography variant="caption">Social links</Typography></Divider>
            {(['facebook', 'instagram', 'twitter'] as const).map((platform) => (
              <TextField
                key={platform}
                label={platform === 'twitter' ? 'X / Twitter' : platform[0].toUpperCase() + platform.slice(1)}
                value={draft.socials[platform] ?? ''}
                disabled={disabled}
                error={Boolean(helperFor(`socials.${platform}`))}
                helperText={helperFor(`socials.${platform}`)}
                onChange={(event) => change((next) => {
                  const value = event.target.value;
                  if (value) next.socials[platform] = value;
                  else delete next.socials[platform];
                })}
              />
            ))}
          </Stack>
        );

      case 'style':
        return (
          <Stack spacing={2}>
            <ColorField
              label="Primary"
              value={draft.theme.primaryColor}
              disabled={disabled}
              onChange={(value) => change((next) => { next.theme.primaryColor = value; })}
            />
            <ColorField
              label="Accent"
              value={draft.theme.secondaryColor}
              disabled={disabled}
              onChange={(value) => change((next) => { next.theme.secondaryColor = value; })}
            />
            <ColorField
              label="Background"
              value={draft.theme.backgroundColor}
              disabled={disabled}
              onChange={(value) => change((next) => { next.theme.backgroundColor = value; })}
            />
            <ColorField
              label="Text"
              value={draft.theme.textColor}
              disabled={disabled}
              onChange={(value) => change((next) => { next.theme.textColor = value; })}
            />
          </Stack>
        );

      case 'seo':
        return (
          <Stack spacing={2}>
            <TextField
              label="Page title"
              value={draft.metadata.title}
              disabled={disabled}
              error={Boolean(helperFor('metadata.title'))}
              helperText={helperFor('metadata.title') || `${draft.metadata.title.length}/70`}
              onChange={(event) => change((next) => { next.metadata.title = event.target.value; })}
            />
            <TextField
              label="Description"
              multiline
              minRows={4}
              value={draft.metadata.description}
              disabled={disabled}
              error={Boolean(helperFor('metadata.description'))}
              helperText={helperFor('metadata.description') || `${draft.metadata.description.length}/200`}
              onChange={(event) => change((next) => { next.metadata.description = event.target.value; })}
            />
            {assetField('favicon', 'Browser icon', 'Only used when there’s no logo')}
            {assetField('sharingImage', 'Sharing image', 'Link previews use the logo; this is only used when there’s no logo')}
            <Alert severity="info">The primary tenant hostname sets the canonical URL.</Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Stack spacing={2.25}>
      <Box>
        <Typography variant="h6" fontWeight={800}>{section.label}</Typography>
        <Typography variant="body2" color="text.secondary">{section.description}</Typography>
      </Box>
      {uploadError ? (
        <Alert severity="error" onClose={() => setUploadError(undefined)}>{uploadError}</Alert>
      ) : null}
      <Dialog open={confirmRemoveHeroVideo} onClose={() => setConfirmRemoveHeroVideo(false)} fullWidth maxWidth="xs">
        <DialogTitle>Remove the background video?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The spotlight will show your background image instead. The video file is deleted
            automatically once neither this draft nor your published site uses it, so upload it
            again if you change your mind later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveHeroVideo(false)}>Keep video</Button>
          <Button color="error" variant="contained" onClick={removeHeroVideo}>Remove video</Button>
        </DialogActions>
      </Dialog>
      {issues.length > 0 ? (
        <Alert severity="warning">Publish checks found items that may need attention.</Alert>
      ) : null}
      {renderSection()}
    </Stack>
  );
};

export default SiteDraftEditor;
